import { useEffect, useMemo, useRef, useState } from 'react';
import { PencilSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { GroupMember } from '@flock-types/chat.types';
import Avatar from '@/components/Avatar';
import { useAuth } from '@/context/AuthContext';
import useGroupMembers from '@/hooks/useGroupMembers';
import useChatGroupActions from '@/hooks/useChatGroupActions';

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold text-flock-navy-900">{title}</h2>
          <button onClick={onClose} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function MemberPickRow({
  member,
  selected,
  onToggle,
}: {
  member: GroupMember;
  selected?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      className={clsx(
        'flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition',
        selected ? 'bg-flock-navy-100' : 'hover:bg-flock-navy-50'
      )}
    >
      <Avatar src={member.profileImageUrl} name={member.userName} sizeClass="h-8 w-8" />
      <span className="flex-1 truncate text-sm text-flock-navy-900">{member.userName}</span>
      {selected !== undefined && (
        <span
          className={clsx(
            'h-4 w-4 rounded border',
            selected ? 'border-flock-navy-600 bg-flock-navy-600' : 'border-flock-gray-300'
          )}
        />
      )}
    </button>
  );
}

interface NewChatMenuProps {
  groupId: string | null;
  /** Navigate to a conversation after creating it. */
  onOpenConversation: (groupId: string, chatGroupId: string) => void;
  onCreated: () => void;
}

/** "+" button in the sidebar header → new chat group or DM. */
export default function NewChatMenu({ groupId, onOpenConversation, onCreated }: NewChatMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [modal, setModal] = useState<'group' | 'dm' | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const { members } = useGroupMembers(groupId);
  const { createChatGroup, createDM } = useChatGroupActions(groupId);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const otherMembers = useMemo(
    () => members.filter((m) => m.userId !== user?.id),
    [members, user?.id]
  );
  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return otherMembers;
    return otherMembers.filter((m) => m.userName.toLowerCase().includes(q));
  }, [otherMembers, search]);

  const reset = () => {
    setName('');
    setDescription('');
    setSelectedIds(new Set());
    setSearch('');
    setError(null);
    setModal(null);
  };

  const handleCreateGroup = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createChatGroup({
        name: name.trim(),
        description: description.trim() || undefined,
        memberIds: Array.from(selectedIds),
      });
      reset();
      onCreated();
      if (groupId && created?.id) onOpenConversation(groupId, created.id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not create chat group.');
    } finally {
      setBusy(false);
    }
  };

  const handleStartDM = async (member: GroupMember) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { chatGroup } = await createDM(member.userId);
      reset();
      onCreated();
      if (groupId && chatGroup?.id) onOpenConversation(groupId, chatGroup.id);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not start the conversation.');
      setBusy(false);
    }
  };

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setMenuOpen((v) => !v)}
        disabled={!groupId}
        title="New conversation"
        className="rounded-lg border border-flock-gray-200 p-2 text-flock-navy-500 transition hover:bg-flock-navy-50 disabled:opacity-50"
      >
        <PencilSquareIcon className="h-5 w-5" />
      </button>

      {menuOpen && (
        <div className="absolute right-0 top-full z-40 mt-1 w-48 overflow-hidden rounded-xl border border-flock-gray-200 bg-white py-1 shadow-xl">
          <button
            onClick={() => {
              setMenuOpen(false);
              setModal('group');
            }}
            className="block w-full px-3 py-2 text-left text-sm text-flock-navy-700 hover:bg-flock-navy-50"
          >
            New chat group
          </button>
          <button
            onClick={() => {
              setMenuOpen(false);
              setModal('dm');
            }}
            className="block w-full px-3 py-2 text-left text-sm text-flock-navy-700 hover:bg-flock-navy-50"
          >
            New direct message
          </button>
        </div>
      )}

      {modal === 'group' && (
        <Modal title="New chat group" onClose={reset}>
          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Group name (e.g. Worship Team)"
            className="mb-2 w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
          />
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            className="mb-3 w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
          />
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-flock-navy-400">
            Add members
          </div>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members"
            className="mb-2 w-full rounded-lg border border-flock-gray-200 px-3 py-1.5 text-sm outline-none focus:border-flock-navy-400"
          />
          <div className="mb-4 max-h-48 space-y-0.5 overflow-y-auto">
            {filteredMembers.map((m) => (
              <MemberPickRow
                key={m.userId}
                member={m}
                selected={selectedIds.has(m.userId)}
                onToggle={() =>
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(m.userId)) next.delete(m.userId);
                    else next.add(m.userId);
                    return next;
                  })
                }
              />
            ))}
            {filteredMembers.length === 0 && (
              <div className="py-3 text-center text-sm text-flock-navy-400">No members found</div>
            )}
          </div>
          <button
            onClick={handleCreateGroup}
            disabled={!name.trim() || busy}
            className="min-h-touch w-full rounded-lg bg-flock-navy-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-flock-navy-700 disabled:opacity-50"
          >
            {busy ? 'Creating…' : 'Create Chat Group'}
          </button>
        </Modal>
      )}

      {modal === 'dm' && (
        <Modal title="New direct message" onClose={reset}>
          {error && (
            <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search members"
            className="mb-2 w-full rounded-lg border border-flock-gray-200 bg-flock-navy-50 px-3 py-2 text-sm outline-none focus:border-flock-navy-400"
          />
          <div className="max-h-64 space-y-0.5 overflow-y-auto">
            {filteredMembers.map((m) => (
              <MemberPickRow key={m.userId} member={m} onToggle={() => handleStartDM(m)} />
            ))}
            {filteredMembers.length === 0 && (
              <div className="py-3 text-center text-sm text-flock-navy-400">No members found</div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
